import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { Subject } from 'rxjs';
import { ResponseCheckStatus } from '../model/background-geolocation';

declare const BackgroundGeolocation;

@Injectable()
export class BackgroundGeolocationNative {

  startSubject$: Subject<string> = new Subject<string>();
  locationSubject$: Subject<Location> = new Subject<Location>();
  activitySubject$: Subject<any> = new Subject<any>();

  constructor(
    private platform: Platform,
  ) {
  }

  async configure(conf: any): Promise<void> {
    if (this.platform.is('cordova')) {
      const config: any = {
        desiredAccuracy: 0,
        locationProvider: this.platform.is('android')
          ? BackgroundGeolocation.ACTIVITY_PROVIDER
          : BackgroundGeolocation.DISTANCE_FILTER_PROVIDER,
        ...conf,
      };
      await BackgroundGeolocation.configure(config,
        () => {
          console.log('success BackgroundGeolocation.configure');
        }, () => {
          console.log('failed BackgroundGeolocation.configure');
        }
      );
    }
  }

  async start(): Promise<void> {
    if (this.platform.is('cordova')) {
      const checkStatus = await this.checkStatus();
      if (checkStatus.isRunning) {
        BackgroundGeolocation.removeAllListeners();

        this.onStart();
        this.onLocation();
        this.onActivity();
        console.log('start() already started with config: ', JSON.stringify(await this.getConfig()));
      } else {
        this.onStart();
        this.onLocation();
        this.onActivity();

        BackgroundGeolocation.start();
        console.log('start() started with config', JSON.stringify(await this.getConfig()));
      }
    }
  }

  stop(): void {
    if (this.platform.is('cordova')) {
      BackgroundGeolocation.removeAllListeners();
      BackgroundGeolocation.stop();
    }
  }

  checkStatus() {
    return new Promise<ResponseCheckStatus>(resolve => {
      const result = {
        isRunning: false,
        authorization: 0,
        locationServicesEnabled: false
      };

      if (this.platform.is('cordova')) {
        BackgroundGeolocation.checkStatus((status) => {
          console.log('[INFO] BackgroundGeolocation service is running', status.isRunning);
          console.log('[INFO] BackgroundGeolocation services enabled', status.locationServicesEnabled);
          console.log('[INFO] BackgroundGeolocation auth status: ' + status.authorization);
        
          result.isRunning = status.isRunning
          result.authorization = status.authorization
          result.locationServicesEnabled = status.locationServicesEnabled
          resolve(result);
        });
      } else {
        resolve(result);
      }
    });
  }

  private onStart(): void {
    if (this.platform.is('cordova')) {
      BackgroundGeolocation.on('start', () => {
        this.startSubject$.next('[INFO] BackgroundGeolocation service has been started');
      });
    }
  }

  private onLocation(): void {
    if (this.platform.is('cordova')) {
      BackgroundGeolocation.on('location', location => {
        this.locationSubject$.next(location);
        console.log('[INFO] BackgroundGeolocation location: ' + JSON.stringify(location));
        BackgroundGeolocation.startTask(taskKey => {
          console.log('[INFO] BackgroundGeolocation taskKey: ' + taskKey);
          BackgroundGeolocation.endTask(taskKey);
        });
      });
    }
  }

  private onActivity(): void {
    if (this.platform.is('cordova')) {
      BackgroundGeolocation.on('activity', activity => {
        this.activitySubject$.next(activity);
      });
    }
  }

  getConfig(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      BackgroundGeolocation.getConfig(
        success => {
          resolve(success)
        },
        error => {
          reject(error)
        })
    })
  }

  getLocations(): Promise<Location[]> {
    return new Promise<Location[]>((resolve, reject) => {
      BackgroundGeolocation.getLocations(
        success => {
          resolve(success)
        },
        error => {
          reject(error)
        })
    })
  }

  getLogEntries(params: ParamsLogEntries): Promise<any[]> {
    return new Promise<any[]>((resolve, reject) => {
      BackgroundGeolocation.getLogEntries(
        params.limit,
        params.fromId,
        params.minLevel,
        success => {
          resolve(success)
        },
        error => {
          reject(error)
        })
    })
  }

  showLocationSettings(): void {
    BackgroundGeolocation.showLocationSettings();
  }

  showAppSettings(): void {
    BackgroundGeolocation.showAppSettings();
  }

  plugin() {
    return BackgroundGeolocation;
  }
}

interface ParamsLogEntries {
  limit: number;
  fromId: number;
  minLevel: Level;
}

type Level = 'TRACE' | 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';



// WEBPACK FOOTER //
// ./src/native/background-geolocation.native.ts